import axios from 'axios';

const apiURL = process.env.REACT_APP_API_URL;

const signalNotesChanged = () => {
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("notes:updated"));
    }
  } catch {
    /* ignore - window not available */
  }

  try {
    localStorage.setItem("notesLastChanged", Date.now().toString());
  } catch {
    /* ignore storage errors */
  }
};

export interface notePriority {
    notePriorityId: number
    name: string
    sortOrder: number
}

/** Ids match the seeded note_priority rows - see docs/migrations/add_note_priority.sql */
export const NOTE_PRIORITY_NORMAL = 2;

export const getNotePriorities = async (): Promise<notePriority[]> => {
    const token = localStorage.getItem('authToken');
    if (!token) {
        throw new Error('Authorization token not found');
    }

    const response = await axios.get<notePriority[]>(`https://${apiURL}/note/priorities`, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + token,
        },
    });

    return response.data;
};

export const updateNotePriority = async (noteId: number, notePriorityId: number) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
        throw new Error('Authorization token not found');
    }

    const response = await axios({
        url: `https://${apiURL}/Note/${noteId}/Priority/${notePriorityId}`,
        method: 'put',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + token,
        },
    });

    signalNotesChanged();
    return response.data;
};

export const getNoteCount = async (): Promise<number> => {
    const token = localStorage.getItem('authToken');
    if (!token) {
        throw new Error('Authorization token not found');
    }

    const response = await axios.get(`https://${apiURL}/note/count`, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + token,
        },
    });

    return response.data;
};

export interface addNoteDTO {
    enquiryId: number | null
    quoteId: number | null
    jobId: number | null
    serviceReminderId?: number | null
    invoiceId?: number | null
    vehicleId?: number | null
    toolId?: number | null
    goNoGoId?: number | null
}

export const addNote = async (sourceEntity: addNoteDTO, note: any, employeesAssignedToId: number, dueDate?: Date, notePriorityId?: number) => {
    const noteDescription = note;
    if (sourceEntity.enquiryId != null) {
        return await addEnquiryNote(sourceEntity.enquiryId, noteDescription, employeesAssignedToId, dueDate, notePriorityId);
    }
    else if (sourceEntity.quoteId != null) {
        return await addQuoteNote(sourceEntity.quoteId, noteDescription, employeesAssignedToId, notePriorityId);
    }
    else if (sourceEntity.jobId != null) {
        return await addJobNote(sourceEntity.jobId, noteDescription, employeesAssignedToId, notePriorityId);
    } if(sourceEntity.serviceReminderId != null) {
        return await addServiceReminderNote(sourceEntity.serviceReminderId, noteDescription, employeesAssignedToId, notePriorityId);
    } else if (sourceEntity.invoiceId != null) {
      return await addInvoiceNote( sourceEntity.invoiceId, noteDescription, employeesAssignedToId, notePriorityId);
    } else if (sourceEntity.vehicleId != null) {
        return await addVehicleNote(sourceEntity.vehicleId, noteDescription, employeesAssignedToId, notePriorityId);
    } else if (sourceEntity.toolId != null) {
        return await addToolNote(sourceEntity.toolId, noteDescription, employeesAssignedToId, notePriorityId);
    } else if (sourceEntity.goNoGoId != null) {
        return await addGoNoGoNote(sourceEntity.goNoGoId, noteDescription, employeesAssignedToId, notePriorityId);
    }
    else {
        return "";
    }
};

export const addStandaloneNote = async (note: string, employeesAssignedToId: number[], dueDate?: Date, notePriorityId?: number) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
        throw new Error('Authorization token not found');
    }

    const response = await axios({
        url: `https://${apiURL}/note`,
        method: 'post',
        data: {
            note: note,
            employeesAssignedToId: employeesAssignedToId,
            dueDate: dueDate || null,
            isCompleted: false,
            notePriorityId: notePriorityId ?? NOTE_PRIORITY_NORMAL
        },
        headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + token,
        }
    });

    signalNotesChanged();
    const data = response.data;
    return data;
};

const addInvoiceNote = async (invoiceId: number, note: any, employeesAssignedToId?: number, notePriorityId?: number) => {
  const token = localStorage.getItem('authToken');
  if (!token) {
      throw new Error('Authorization token not found');
  }

  const response = await axios({
  url: `https://${apiURL}/Invoice/Notes/${invoiceId}`,
  method: 'post',
  data: {
    note: note,
    employeesAssignedToId: employeesAssignedToId,
    notePriorityId: notePriorityId ?? NOTE_PRIORITY_NORMAL
   },
  headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + token,
  }
  });

  const data = response.data;
  return data;
};


const addEnquiryNote = async (enquiryId: number, note: any, employeesAssignedToId?: number, dueDate?: Date, notePriorityId?: number) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
        throw new Error('Authorization token not found');
    }

    const response = await axios({
    url: `https://${apiURL}/Enquiries/Notes/${enquiryId}`,
    method: 'post',
    data: {
      note: note,
      employeesAssignedToId: employeesAssignedToId,
      dueDate: dueDate || null,
      notePriorityId: notePriorityId ?? NOTE_PRIORITY_NORMAL
     },
    headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token,
    }
    });

    const data = response.data;
    return data;
};
const addQuoteNote = async (quoteId: number, note: any, employeesAssignedToId?: number, notePriorityId?: number) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
       throw new Error('Authorization token not found');
    }
 
    const response = await axios({
       url: `https://${apiURL}/Quotes/Notes/${quoteId}`,
       method: 'post',
       data: {
        note: note,
        employeesAssignedToId: employeesAssignedToId,
        notePriorityId: notePriorityId ?? NOTE_PRIORITY_NORMAL
       },
       headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token,
       }
    });
 
    const data = response.data;
    return data;
};
const addJobNote = async (jobId: number, note: any, employeesAssignedToId?: number, notePriorityId?: number) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
       throw new Error('Authorization token not found');
    }
 
    const response = await axios({
       url: `https://${apiURL}/Jobs/Notes/${jobId}`,
       method: 'post',
       data: {
        note: note,
        employeesAssignedToId: employeesAssignedToId,
        notePriorityId: notePriorityId ?? NOTE_PRIORITY_NORMAL
       },
       headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token,
       }
    });
 
    const data = response.data;
    return data;
};

const addServiceReminderNote = async (jobId: number, note: any, employeesAssignedToId?: number, notePriorityId?: number) => {
  const token = localStorage.getItem('authToken');
  if (!token) {
     throw new Error('Authorization token not found');
  }

  console.log("note", note);

  const response = await axios({
     url: `https://${apiURL}/ServiceReminder/Notes/${jobId}`,
     method: 'post',
     data: {
      note: note,
      employeesAssignedToId: employeesAssignedToId,
      notePriorityId: notePriorityId ?? NOTE_PRIORITY_NORMAL
     },
     headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token,
     }
  });

  const data = response.data;
  return data;
};


const getServiceReminderNotes = async (quoteId: number) : Promise<notesGridDTO[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  let gridData: notesGridDTO[] = [];
  await axios.get<notesGridDTO[]>(
    `https://${apiURL}/ServiceReminder/Notes/${quoteId}`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  ).then((notes) => {
      gridData = notes.data;
  }).catch((err) => {
      console.log(JSON.stringify(err, null, 2));
  });

  return gridData;
}

export interface notesGridDTO {
    noteId: number
    note: string
    createdDate: Date
    dueDate?: Date
    createdBy: string
    noteType: string
    employeesAssignedToId: string
    isCompleted: boolean
    notePriorityId: number
    /** High / Normal / Low */
    notePriority: string
    notePrioritySortOrder: number
    assignedTo: string
    assignedEmployees: AssignedEmployee[]
    enquiryId?: number
    quotId?: number
    jobId?: number
    invoiceId?: number
    serviceReminderId?: number
    reference?: string
    clientName?: string
    siteAddress?: string
    assignments?: NoteAssignment[]
}

export interface NoteAssignment {
    employeeId: number
    employeeName: string
    isCompleted: boolean
    completedDate?: string | null
}

interface AssignedEmployee {
  employeeId: number
  employeeName: string
}

export const GetNotes = async(sourceEntity: addNoteDTO) => {
    let gridData: notesGridDTO[] = [];

    if (sourceEntity.enquiryId != null) {
        await getEnquiryNotes(sourceEntity.enquiryId).then((response) => {
            gridData = response;
        });        
    }
    else if (sourceEntity.quoteId != null) {
        await getQuoteNotes(sourceEntity.quoteId).then((response) => {
            gridData = response;
        })
    }
    else if (sourceEntity.jobId != null) {
        await getJobNotes(sourceEntity.jobId).then((response) => {
            gridData = response;
        })
    }
    else if(sourceEntity.serviceReminderId != null) {
      await getServiceReminderNotes(sourceEntity.serviceReminderId).then((response) => {
          gridData = response;
      })
    }
    else if(sourceEntity.invoiceId != null) {
      await getInvoiceNotes(sourceEntity.invoiceId).then((response) => {
          gridData = response;
      })
    }
    else if(sourceEntity.vehicleId != null) {
      await getVehicleNotes(sourceEntity.vehicleId).then((response) => {
          gridData = response;
      })
    }
    else if(sourceEntity.toolId != null) {
      await getToolNotes(sourceEntity.toolId).then((response) => {
          gridData = response;
      })
    }
    else if(sourceEntity.goNoGoId != null) {
      await getGoNoGoNotes(sourceEntity.goNoGoId).then((response) => {
          gridData = response;
      })
    }

    return gridData;
};

const getEnquiryNotes = async (enquiryId: number) : Promise<notesGridDTO[]> => {
    const token = localStorage.getItem('authToken')

    if (!token) {
      throw new Error('Authorization token not found')
    }
  
    let gridData: notesGridDTO[] = [];
    await axios.get<notesGridDTO[]>(
      `https://${apiURL}/Enquiries/Notes/${enquiryId}`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
      }
    ).then((notes) => {
        gridData = notes.data;
    }).catch((err) => {
        console.log(JSON.stringify(err, null, 2));
    });

    return gridData;
}
const getQuoteNotes = async (quoteId: number) : Promise<notesGridDTO[]> => {
    const token = localStorage.getItem('authToken')

    if (!token) {
      throw new Error('Authorization token not found')
    }
  
    let gridData: notesGridDTO[] = [];
    await axios.get<notesGridDTO[]>(
      `https://${apiURL}/Quotes/Notes/${quoteId}`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
      }
    ).then((notes) => {
        gridData = notes.data;
    }).catch((err) => {
        console.log(JSON.stringify(err, null, 2));
    });

    return gridData;
}
const getJobNotes = async (jobId: number) : Promise<notesGridDTO[]> => {
    const token = localStorage.getItem('authToken')

    if (!token) {
      throw new Error('Authorization token not found')
    }
  
    let gridData: notesGridDTO[] = [];
    await axios.get<notesGridDTO[]>(
      `https://${apiURL}/Jobs/Notes/${jobId}`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
      }
    ).then((notes) => {
        gridData = notes.data;
    }).catch((err) => {
        console.log(JSON.stringify(err, null, 2));
    });

    return gridData;
}
const getInvoiceNotes = async (invoiceId: number) : Promise<notesGridDTO[]> => {
    const token = localStorage.getItem('authToken')

    if (!token) {
      throw new Error('Authorization token not found')
    }
  
    let gridData: notesGridDTO[] = [];
    await axios.get<notesGridDTO[]>(
      `https://${apiURL}/Invoice/Notes/${invoiceId}`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
      }
    ).then((notes) => {
        gridData = notes.data;
    }).catch((err) => {
        console.log(JSON.stringify(err, null, 2));
    });

    return gridData;
}
export const deleteNote = async (noteId: number) => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }
  try {
    const response = await axios({
      url: `https://${apiURL}/Note/${noteId}`,
      method: 'delete',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    })

    const data: notesGridDTO = response.data

    signalNotesChanged();

    return data
  } catch {
    throw new Error('Error deleting note');
  } 
}



export const toggleCompletedNote = async (noteId: number) => {
  const token = localStorage.getItem('authToken');
  if (!token) {
     throw new Error('Authorization token not found');
  }


  const response = await axios({
     url: `https://${apiURL}/Note/${noteId}`,
     method: 'put',
     headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token,
     }
  });

  const data = response.data;
  signalNotesChanged();
  return data;
};

export const getNotesWithPagination = async (params: {
  sourceEntity: addNoteDTO
  skip: number
  take: number
  sort: any[]
  filter: any
  showCompleted?: boolean
  /** Restricts to one priority; omit or 0 for all priorities. */
  notePriorityId?: number | null
  /**
   * Whose notes to show. Omit for the signed in user's own. The API refuses any
   * employee they have not been granted access to, so this only widens the view
   * where the grant actually exists.
   */
  viewEmployeeId?: number | null
}): Promise<{ data: notesGridDTO[], total: number }> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const sourceEntity = params.sourceEntity

  // If viewing notes for a specific entity (enquiry/quote/job/invoice), use the entity-specific endpoint
  // Otherwise, use the paginated endpoint for the /notes page
  if (sourceEntity.enquiryId != null || sourceEntity.quoteId != null || sourceEntity.jobId != null || sourceEntity.invoiceId != null || sourceEntity.serviceReminderId != null || sourceEntity.vehicleId != null || sourceEntity.toolId != null || sourceEntity.goNoGoId != null) {
    // Use GetNotes for entity-specific views - returns all notes for that entity
    const notes = await GetNotes(sourceEntity)
    return {
      data: notes,
      total: notes.length
    }
  }

  // For the /notes page, use the paginated endpoint
  const queryParams = new URLSearchParams({
    skip: params.skip.toString(),
    take: params.take.toString(),
  })

  // Get the current employee ID from stored user data
  const user = localStorage.getItem('user')
  let employeeId: number | null = null
  if (user) {
    try {
      const userData = JSON.parse(user)
      // Get the actual employeeId (numeric)
      employeeId = userData.employeeId || null
    } catch {
      // Ignore parsing errors
    }
  }

  // Add employeeId if available
  if (employeeId !== null) {
    queryParams.append('employeeId', employeeId.toString())
  }

  // Whose notes to show. Sent only when looking at somebody else, so the
  // ordinary case is the same request it has always been.
  if (params.viewEmployeeId && params.viewEmployeeId !== employeeId) {
    queryParams.append('viewEmployeeId', params.viewEmployeeId.toString())
  }

  // Add showCompleted parameter (always include it)
  queryParams.append('showCompleted', params.showCompleted ? 'true' : 'false')

  // Priority filter - omitted entirely when showing all priorities
  if (params.notePriorityId) {
    queryParams.append('notePriorityId', params.notePriorityId.toString())
  }

  // Add sorting parameters if present
  if (params.sort && params.sort.length > 0) {
    // Use only the first sort item (primary sort)
    const primarySort = params.sort[0]
    queryParams.append('sortField', primarySort.field)
    queryParams.append('sortDir', primarySort.dir)
  }

  // Add filter parameters if present
  if (params.filter) {
    console.log('Filter object received in getNotesWithPagination:', JSON.stringify(params.filter, null, 2));
    
    // The filter should already be in the correct structure from handleFilterChange
    // Just pass it directly to the API
    const filterToSend = params.filter;
    
    console.log('Filter being sent to API:', JSON.stringify(filterToSend, null, 2));
    queryParams.append('filter', JSON.stringify(filterToSend))
  }

  const url = `https://${apiURL}/note/paginated?${queryParams.toString()}`
  console.log('Fetching notes with URL:', url);

  const response = await axios.get<{ data: notesGridDTO[], total: number }>(
    url,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )

  return {
    data: response.data.data,
    total: response.data.total
  }
}

export const getNoteTypesForFilterDropdown = async (searchTerm: string): Promise<{text: string, value: string}[]> => {
  const types = [
    { text: 'Enquiry', value: 'Enquiry' },
    { text: 'Quote', value: 'Quote' },
    { text: 'Job', value: 'Job' },
    { text: 'Invoice', value: 'Invoice' },
    { text: 'Service Reminder', value: 'ServiceReminder' },
    { text: 'General', value: 'General' }
  ]

  if (!searchTerm) {
    return types
  }

  return types.filter(t => t.text.toLowerCase().includes(searchTerm.toLowerCase()))
}

export const getNoteDetailsForFilterDropdown = async (sourceEntity: addNoteDTO, searchTerm: string): Promise<{text: string, value: string}[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const allNotes = await GetNotes(sourceEntity)
  
  const details = Array.from(new Set(allNotes.map(n => n.note)))
    .filter(note => !searchTerm || note.toLowerCase().includes(searchTerm.toLowerCase()))
    .map(note => ({ text: note, value: note }))
    .sort((a, b) => a.text.localeCompare(b.text))

  return details
}

export const getNoteReferencesForFilterDropdown = async (sourceEntity: addNoteDTO, searchTerm: string): Promise<{text: string, value: string}[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  // For references, we would need to track the parent entity references
  // For now, return empty as this would require additional API calls
  return []
}

// Vehicle Notes Functions
export const getVehicleNotes = async (vehicleId: number) => {
    const token = localStorage.getItem('authToken');
    
    // Get API URL - check both localStorage and env variable
    const storedApiURL = localStorage.getItem('apiURL');
    const finalApiURL = storedApiURL || apiURL;
    const url = `https://${finalApiURL}/Vehicle/Notes/${vehicleId}`;
    
    console.log('Fetching vehicle notes from:', url);
    
    const config = {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        }
    };
    
    try {
        const response = await axios.get(url, config);
        return response.data;
    } catch (error) {
        console.error('Error fetching vehicle notes:', error);
        return [];
    }
};

export const addVehicleNote = async (vehicleId: number, note: any, employeesAssignedToId: number, notePriorityId?: number) => {
    const token = localStorage.getItem('authToken');
    
    // Get API URL - check both localStorage and env variable
    const storedApiURL = localStorage.getItem('apiURL');
    const finalApiURL = storedApiURL || apiURL;
    const url = `https://${finalApiURL}/Vehicle/Notes/${vehicleId}`;
    
    console.log('API URL:', finalApiURL);
    console.log('Full URL:', url);
    console.log('Token:', token ? 'Present' : 'Missing');
    
    const config = {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        }
    };

    const noteData = {
        note: note,
        employeesAssignedToId: [parseInt(employeesAssignedToId.toString(), 10)],
        isCompleted: false,
        notePriorityId: notePriorityId ?? NOTE_PRIORITY_NORMAL
    };

    console.log('Adding vehicle note:', noteData);
    console.log('Employee ID type:', typeof employeesAssignedToId, 'Value:', employeesAssignedToId);

    try {
        const response = await axios.post(url, noteData, config);
        signalNotesChanged();
        return response.data;
    } catch (error) {
        console.error('Error adding vehicle note:', error);
        if (axios.isAxiosError(error)) {
            console.error('Response data:', error.response?.data);
            console.error('Response status:', error.response?.status);
            console.error('Request URL:', error.config?.url);
        }
        throw error;
    }
};

export const getToolNotes = async (toolId: number) => {
    const token = localStorage.getItem('authToken');
    const storedApiURL = localStorage.getItem('apiURL');
    const finalApiURL = storedApiURL || apiURL;

    const config = {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        }
    };

    try {
        const response = await axios.get(`https://${finalApiURL}/Tool/Notes/${toolId}`, config);
        return response.data;
    } catch (error) {
        console.error('Error fetching tool notes:', error);
        return [];
    }
};

export const addToolNote = async (toolId: number, note: any, employeesAssignedToId: number, notePriorityId?: number) => {
    const token = localStorage.getItem('authToken');
    const storedApiURL = localStorage.getItem('apiURL');
    const finalApiURL = storedApiURL || apiURL;

    const config = {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        }
    };

    const noteData = {
        note: note,
        employeesAssignedToId: [parseInt(employeesAssignedToId.toString(), 10)],
        isCompleted: false,
        notePriorityId: notePriorityId ?? NOTE_PRIORITY_NORMAL
    };

    try {
        const response = await axios.post(`https://${finalApiURL}/Tool/Notes/${toolId}`, noteData, config);
        signalNotesChanged();
        return response.data;
    } catch (error) {
        console.error('Error adding tool note:', error);
        throw error;
    }
};

export const getGoNoGoNotes = async (goNoGoId: number) => {
    const token = localStorage.getItem('authToken');
    const storedApiURL = localStorage.getItem('apiURL');
    const finalApiURL = storedApiURL || apiURL;

    const config = {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        }
    };

    try {
        const response = await axios.get(`https://${finalApiURL}/GoNoGo/Notes/${goNoGoId}`, config);
        return response.data;
    } catch (error) {
        console.error('Error fetching go no go notes:', error);
        return [];
    }
};

export const addGoNoGoNote = async (goNoGoId: number, note: any, employeesAssignedToId: number, notePriorityId?: number) => {
    const token = localStorage.getItem('authToken');
    const storedApiURL = localStorage.getItem('apiURL');
    const finalApiURL = storedApiURL || apiURL;

    const config = {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        }
    };

    const noteData = {
        note: note,
        employeesAssignedToId: [parseInt(employeesAssignedToId.toString(), 10)],
        isCompleted: false,
        notePriorityId: notePriorityId ?? NOTE_PRIORITY_NORMAL
    };

    try {
        const response = await axios.post(`https://${finalApiURL}/GoNoGo/Notes/${goNoGoId}`, noteData, config);
        signalNotesChanged();
        return response.data;
    } catch (error) {
        console.error('Error adding go no go note:', error);
        throw error;
    }
};

export interface viewableEmployee {
  employeeId: number
  employeeName: string
}

/**
 * The employees whose notes the signed in user may look at - themselves first,
 * then anyone granted to them. A single entry means they have no grants and the
 * notes screen shows no picker.
 */
export const getViewableEmployees = async (): Promise<viewableEmployee[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get<viewableEmployee[]>(
    `https://${apiURL}/Note/ViewableEmployees`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )

  return response.data
}
